import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { jobKeys } from '~/domain/Jobs/jobKeys';
import { jobsService } from '~/domain/Jobs/jobsService';
import { DefaultGeneralSpecialty } from '../../../constants';
import {
  CANDIDATES_POLL_INTERVAL_MS,
  candidateSortOptions,
  defaultFilters,
  getCandidatePosition,
} from '../utils';

export const getDefaultCandidateFilters = (specialty) => {
  const isGeneralDentistryJob = specialty === DefaultGeneralSpecialty;

  return {
    ...defaultFilters,
    sort: !isGeneralDentistryJob
      ? candidateSortOptions.skillsMatch
      : candidateSortOptions.localFavorites,
  };
};

export const getCandidatePage = (allCandidates = [], filters = defaultFilters) => {
  const limit = filters?.limit || defaultFilters.limit;
  const totalPages = Math.max(Math.ceil(allCandidates.length / limit), 1);
  const page = Math.min(Math.max(filters?.page || 1, 1), totalPages);

  return {
    filters: {
      ...defaultFilters,
      ...filters,
      limit,
      page,
      totalPages,
    },
    visibleCandidates: allCandidates.slice((page - 1) * limit, page * limit),
  };
};

export const getCandidatePageNumber = (
  allCandidates = [],
  candidateId,
  limit = defaultFilters.limit,
) => getCandidatePosition(allCandidates, candidateId, limit).candidatePage ?? null;

export const useJobCandidates = ({ jobId: providedJobId, specialty, enabled = true } = {}) => {
  const params = useParams();
  const queryClient = useQueryClient();
  const jobId = providedJobId ?? params.jobId;
  const [filters, setFilters] = useState(() => getDefaultCandidateFilters(specialty));

  useEffect(() => {
    if (specialty) {
      setFilters(getDefaultCandidateFilters(specialty));
    }
  }, [specialty]);

  const isQueryEnabled = Boolean(jobId && specialty && enabled);

  const candidatesQuery = useQuery({
    queryKey: jobKeys.candidates(jobId ?? ''),
    queryFn: () => jobsService.getJobCandidates(jobId),
    enabled: isQueryEnabled,
    staleTime: CANDIDATES_POLL_INTERVAL_MS,
    gcTime: 1000 * 60 * 2,
    // Poll while the screen is open: query-core's focusManager only listens for
    // visibilitychange, so an office sitting on this page gets no trigger at all
    // (GOTU-8310). Cadence and staleTime share one constant — see its doc for why.
    refetchInterval: CANDIDATES_POLL_INTERVAL_MS,
    retry: false,
  });

  const allCandidates = candidatesQuery.data ?? [];
  const { filters: normalizedFilters, visibleCandidates } = useMemo(
    () => getCandidatePage(allCandidates, filters),
    [allCandidates, filters],
  );

  useEffect(() => {
    if (
      normalizedFilters.page !== filters.page ||
      normalizedFilters.totalPages !== filters.totalPages
    ) {
      setFilters(normalizedFilters);
    }
  }, [filters, normalizedFilters]);

  const reloadCandidates = useCallback(
    async (requestedJobId = jobId) => {
      if (!requestedJobId) {
        return undefined;
      }

      return queryClient.invalidateQueries({ queryKey: jobKeys.candidates(requestedJobId) });
    },
    [jobId, queryClient],
  );

  const showCandidatePage = useCallback(
    (candidateId) => {
      const candidatePage = getCandidatePageNumber(allCandidates, candidateId, filters.limit);

      if (!candidatePage || candidatePage === filters.page) {
        return;
      }

      setFilters((currentFilters) => ({
        ...currentFilters,
        page: candidatePage,
      }));
    },
    [allCandidates, filters.limit, filters.page],
  );

  return {
    reloadCandidates,
    showCandidatePage,
    allCandidates,
    candidates: visibleCandidates,
    visibleCandidates,
    filters: normalizedFilters,
    // Consumed as `isBusy`, which swaps the whole screen for a spinner, so a background
    // poll must not count as loading — only the cold load, when there is nothing to show.
    isLoading: candidatesQuery.isLoading,
    // "We have the candidates", not "the last request succeeded". The two diverge now
    // that this query polls: query-core keeps the previous data on a failed refetch but
    // flips status to 'error', and callers gate the rendered list and the empty-state
    // copy on this flag — so reading isSuccess would blank the candidate strip, and
    // claim a load failure, for up to one poll interval after a single dropped request.
    isSuccessFetchJobCandidates: candidatesQuery.data !== undefined,
    isError: candidatesQuery.isError,
    error: candidatesQuery.error,
  };
};
